import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../utils/api';
import { sortEmployeesByDepartmentAndId, formatDateThai } from '../utils/helpers';
import { 
  Briefcase, Calendar, MapPin, FileText, Users, 
  Search, Plus, Loader2, Save,
  Edit, Trash2, X, Filter
} from 'lucide-react';
// 1. Import SweetAlert2
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const OfficialBusinessManagement = ({ currentUser }) => {
  // Form State
  const [editId, setEditId] = useState(null); 
  const [topic, setTopic] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  // Data State
  const [employees, setEmployees] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const fetchData = async () => {
    setLoading(true);
    try {
      const [empData, tripData] = await Promise.all([
        api.getEmployees(),
        api.getOfficialBusinessTrips()
      ]);
      
      const activeEmployees = empData.filter(e => e.is_active && e.position !== 'ผู้ดูแลระบบ');
      setEmployees(sortEmployeesByDepartmentAndId(activeEmployees));
      setTrips(tripData);
    } catch (error) {
      console.error("Error fetching data:", error);
      MySwal.fire({
        icon: 'error',
        title: 'ผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลได้',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const currentYear = new Date().getFullYear();
  const allYears = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const filteredTrips = useMemo(() => {
    return trips.filter(trip => {
      const tripYear = new Date(trip.start_date).getFullYear();
      return tripYear === selectedYear;
    });
  }, [trips, selectedYear]);

  const filteredEmployees = useMemo(() => {
    if (!searchTerm) return employees;
    const lower = searchTerm.toLowerCase();
    return employees.filter(e => 
      e.first_name.toLowerCase().includes(lower) ||
      e.last_name.toLowerCase().includes(lower) ||
      (e.department || '').toLowerCase().includes(lower)
    );
  }, [employees, searchTerm]);

  const handleToggleEmployee = (id) => {
    setSelectedEmployees(prev => 
      prev.includes(id) ? prev.filter(empId => empId !== id) : [...prev, id]
    );
  };

  const handleSelectAllFiltered = () => {
    const idsToAdd = filteredEmployees.map(e => e.employee_id);
    const newSelected = [...new Set([...selectedEmployees, ...idsToAdd])];
    setSelectedEmployees(newSelected);
  };

  const handleClearSelection = () => {
    setSelectedEmployees([]);
  };

  const handleEdit = (trip) => {
    setEditId(trip.id);
    setTopic(trip.topic);
    setLocation(trip.location);
    setDescription(trip.description || '');
    setStartDate(trip.start_date);
    setEndDate(trip.end_date);
    setSelectedEmployees(trip.employee_ids || []);
    
    // เลื่อนขึ้นด้านบนเพื่อให้พร้อมแก้ไข
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditId(null);
    setTopic('');
    setLocation('');
    setDescription('');
    setStartDate('');
    setEndDate('');
    setSelectedEmployees([]);
  };

  const handleDelete = async (id) => {
    // ใช้ SweetAlert2 แทน window.confirm
    const result = await MySwal.fire({
      title: 'ยืนยันการลบ?',
      text: "คุณต้องการลบรายการนี้ใช่หรือไม่? (หมายเหตุ: การลบจะไม่ยกเลิกเวลาเข้างานที่บันทึกไปแล้วในประวัติ)",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'ใช่, ลบเลย!',
      cancelButtonText: 'ยกเลิก'
    });

    if (result.isConfirmed) {
      try {
          const response = await api.deleteOfficialBusinessTrip(id);
          if (response.success) {
              await MySwal.fire({
                icon: 'success',
                title: 'สำเร็จ!',
                text: 'ลบข้อมูลเรียบร้อยแล้ว',
                timer: 1500,
                showConfirmButton: false
              });
              fetchData(); 
              if (editId === id) handleCancelEdit(); 
          } else {
              MySwal.fire({
                icon: 'error',
                title: 'ลบไม่สำเร็จ',
                text: response.error || 'เกิดข้อผิดพลาดจากเซิร์ฟเวอร์',
              });
          }
      } catch (error) {
          console.error(error);
          MySwal.fire({
            icon: 'error',
            title: 'ผิดพลาด',
            text: 'เกิดข้อผิดพลาดในการเชื่อมต่อ',
          });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // ตรวจสอบความครบถ้วนด้วย SweetAlert2
    if (!topic || !startDate || !endDate || selectedEmployees.length === 0) {
      MySwal.fire({
        icon: 'warning',
        title: 'ข้อมูลไม่ครบถ้วน',
        text: 'กรุณากรอกข้อมูลให้ครบถ้วนและเลือกบุคลากรอย่างน้อย 1 ท่าน',
      });
      return;
    }

    if (new Date(endDate) < new Date(startDate)) {
        MySwal.fire({
          icon: 'warning',
          title: 'วันที่ไม่ถูกต้อง',
          text: 'วันที่สิ้นสุดต้องไม่น้อยกว่าวันที่เริ่มต้น',
        });
        return;
    }

    setSubmitting(true);

    try {
      const payload = {
        topic,
        location,
        description,
        start_date: startDate,
        end_date: endDate,
        employee_ids: selectedEmployees,
        created_by: currentUser.employee_id
      };

      let response;
      if (editId) {
        response = await api.updateOfficialBusinessTrip({ ...payload, id: editId });
      } else {
        response = await api.createOfficialBusinessTrip(payload);
      }

      if (response.success) {
        await MySwal.fire({
          icon: 'success',
          title: 'สำเร็จ!',
          text: editId ? 'แก้ไขข้อมูลเรียบร้อยแล้ว' : 'บันทึกข้อมูลเรียบร้อยแล้ว',
          timer: 1500,
          showConfirmButton: false
        });

        handleCancelEdit(); 
        await fetchData(); // โหลดข้อมูลใหม่ล่าสุดมาแสดง
        
        // เลื่อนหน้าจอกลับขึ้นไปด้านบนสุด
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
      } else {
        MySwal.fire({
          icon: 'error',
          title: 'ไม่สามารถบันทึกได้',
          text: response.error || 'เกิดข้อผิดพลาดในการบันทึก',
        });
      }
    } catch (error) {
      console.error(error);
      MySwal.fire({
        icon: 'error',
        title: 'ผิดพลาด',
        text: 'เกิดข้อผิดพลาดในการเชื่อมต่อ',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <Briefcase className="h-8 w-8 mr-3 text-blue-600" />
          จัดการข้อมูลไปราชการ
        </h1>
        <p className="text-gray-600 mt-2">
          บันทึก/แก้ไข ข้อมูลการไปราชการ อบรม หรือสัมมนา (ระบบจะลงเวลาให้อัตโนมัติ)
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Column: Form */}
        <div className="lg:col-span-1 space-y-6">
          <div className={`rounded-lg shadow overflow-hidden ${editId ? 'bg-blue-50 border-2 border-blue-200' : 'bg-white'}`}>
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                {editId ? <Edit className="h-5 w-5 mr-2 text-blue-600" /> : <Plus className="h-5 w-5 mr-2" />}
                {editId ? 'แก้ไขรายการ' : 'สร้างรายการใหม่'}
              </h2>
              {editId && (
                  <button onClick={handleCancelEdit} className="text-sm text-gray-500 hover:text-red-500 flex items-center transition-colors">
                      <X className="h-4 w-4 mr-1" /> ยกเลิก
                  </button>
              )}
            </div>
            
            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">หัวข้อ/เรื่อง *</label>
                  <input
                    type="text"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                    placeholder="เช่น เข้าร่วมอบรม..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">สถานที่ *</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                      placeholder="ระบุสถานที่"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">เริ่มวันที่ *</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ถึงวันที่ *</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                      required
                      min={startDate}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">รายละเอียดเพิ่มเติม</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                    placeholder="รายละเอียดอื่นๆ (ถ้ามี)"
                  />
                </div>

                {/* Employee Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    เลือกบุคลากร ({selectedEmployees.length} คน) *
                  </label>
                  
                  <div className="border rounded-md p-2 bg-gray-50 border-gray-300">
                    <div className="relative mb-2">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                        <input 
                            type="text"
                            placeholder="ค้นหาชื่อ..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500 transition-shadow"
                        />
                    </div>
                    
                    <div className="flex justify-between text-xs mb-2 px-1">
                        <button type="button" onClick={handleSelectAllFiltered} className="text-blue-600 hover:text-blue-800 font-medium transition-colors">เลือกทั้งหมด (ที่ค้นหา)</button>
                        <button type="button" onClick={handleClearSelection} className="text-red-600 hover:text-red-800 font-medium transition-colors">ล้างค่า</button>
                    </div>

                    <div className="max-h-48 overflow-y-auto bg-white border rounded divide-y border-gray-200">
                        {filteredEmployees.map(emp => (
                            <label key={emp.employee_id} className="flex items-center p-2 hover:bg-blue-50 cursor-pointer transition-colors">
                                <input 
                                    type="checkbox"
                                    checked={selectedEmployees.includes(emp.employee_id)}
                                    onChange={() => handleToggleEmployee(emp.employee_id)}
                                    className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                                />
                                <span className="ml-3 text-sm text-gray-700">
                                    {emp.first_name} {emp.last_name}
                                    <span className="text-xs text-gray-500 block mt-0.5">{emp.department}</span>
                                </span>
                            </label>
                        ))}
                        {filteredEmployees.length === 0 && (
                            <div className="p-4 text-center text-sm text-gray-500">ไม่พบรายชื่อ</div>
                        )}
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting || loading}
                  className={`w-full flex justify-center items-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white transition-colors
                    ${editId ? 'bg-orange-600 hover:bg-orange-700' : 'bg-blue-600 hover:bg-blue-700'} 
                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50`}
                >
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5 mr-2" />}
                  {editId ? 'บันทึกการแก้ไข' : 'บันทึกข้อมูล'}
                </button>

              </form>
            </div>
          </div>
        </div>

        {/* Right Column: History List */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-3 sm:space-y-0">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <FileText className="h-5 w-5 mr-2 text-blue-500" />
                ประวัติการไปราชการ
              </h2>
              
              <div className="flex items-center bg-white border border-gray-300 rounded-md px-3 py-1.5 shadow-sm">
                <Filter className="h-4 w-4 mr-2 text-gray-500" />
                <span className="text-sm text-gray-600 mr-2 font-medium">ปีปฏิทิน:</span>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="block text-sm border-none focus:outline-none focus:ring-0 text-gray-900 font-medium cursor-pointer"
                >
                  {allYears.map(year => (
                    <option key={year} value={year}>{year + 543}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">วันที่</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">เรื่อง/สถานที่</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ผู้เข้าร่วม</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">จัดการ</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                        <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-blue-500" />
                            กำลังโหลดข้อมูล...
                        </td>
                    </tr>
                  ) : filteredTrips.length === 0 ? (
                    <tr>
                        <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                            ไม่พบข้อมูลการไปราชการในปี {selectedYear + 543}
                        </td>
                    </tr>
                  ) : (
                    filteredTrips.map((trip) => (
                      <tr key={trip.id} className={`hover:bg-gray-50 transition-colors ${editId === trip.id ? 'bg-blue-50/50' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 align-top">
                          <div className="font-medium text-blue-600">{formatDateThai(trip.start_date)}</div>
                          {trip.start_date !== trip.end_date && (
                              <div className="text-gray-500 text-xs mt-0.5">ถึง {formatDateThai(trip.end_date)}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 align-top">
                          <div className="font-semibold text-gray-800">{trip.topic}</div>
                          <div className="text-gray-500 flex items-start mt-1.5">
                            <MapPin className="h-3.5 w-3.5 mr-1.5 mt-0.5 flex-shrink-0" /> 
                            <span>{trip.location}</span>
                          </div>
                          {trip.description && (
                              <div className="text-gray-400 text-xs mt-1.5 bg-gray-50 p-2 rounded">{trip.description}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700 align-top max-w-xs">
                          <div className="flex items-start">
                            <Users className="h-4 w-4 mr-2 mt-0.5 text-gray-400 flex-shrink-0" />
                            <span className="line-clamp-2 hover:line-clamp-none transition-all cursor-pointer" title={trip.participants}>
                                {trip.participants || '-'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium align-top">
                            <div className="flex justify-end space-x-2">
                                <button 
                                    onClick={() => handleEdit(trip)}
                                    disabled={editId === trip.id}
                                    className={`p-1.5 rounded-md transition-colors ${editId === trip.id ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-100'}`}
                                    title="แก้ไข"
                                >
                                    <Edit className="h-4 w-4" />
                                </button>
                                <button 
                                    onClick={() => handleDelete(trip.id)}
                                    className="p-1.5 text-red-600 hover:bg-red-100 rounded-md transition-colors"
                                    title="ลบ"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default OfficialBusinessManagement;